import dayjs from 'dayjs'
import React, { useCallback, useMemo, useState } from 'react'

import { Icon, IconButton } from '@/shared/components'
import { Button } from '@/shared/components/Button/Button'
import { TabbedWindow, TabItem } from '@/shared/components/TabbedWindow/TabbedWindow'
import { pxToRem } from '@/shared/styles/themes'
import { faMapMarker, faPause, faPlay, faPlus, faRoute, faStepBackward, faStepForward, faTachometerAlt } from '@fortawesome/pro-regular-svg-icons'
import { faDownLeftAndUpRightToCenter, faTimes, faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons'
import { Box, Divider, Paper, Slider, Switch, Typography } from '@mui/material'

import { useReplayContext } from './ReplayContext'
import { ReplaySettingsDialog } from './ReplaySettingsDialog'

/**
 * Control panel component for historical replay functionality
 * Optimized for performance when dealing with large trace datasets
 */
export const ReplayControlPanel: React.FC = () => {
  const {
    isLoading,
    traces,
    activeTraceIndex,
    isReplaying,
    playbackSpeed,
    currentPointIndex,
    toggleReplay,
    setPlaybackSpeed,
    jumpToPoint,
    toggleProcessedPoints,
    toggleRawPoints,
    toggleRoute,
    showProcessedPoints,
    showRawPoints,
    showRoute,
    setIsReplayPanelVisible,
    setActiveTraceIndex,
    clearReplayData,
    loadReplayData,
  } = useReplayContext()

  // Add a state to track if panel is collapsed
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false)

  // Handle closing the panel
  const handleClosePanel = useCallback(() => {
    setIsReplayPanelVisible(false)
  }, [setIsReplayPanelVisible])

  // Toggle collapse/expand panel
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev)
  }, [])

  // Handle opening the settings dialog
  const handleOpenSettings = useCallback(() => {
    setIsSettingsDialogOpen(true)
  }, [])

  // Handle closing the settings dialog
  const handleCloseSettings = useCallback(() => {
    setIsSettingsDialogOpen(false)
  }, [])

  // Transform traces to tab items for the TabbedWindow component
  const tabItems: TabItem[] = useMemo(() =>
    traces.map((trace, idx) => ({
      id: `${trace.runId}-${idx}`,
      label: trace.runName,
      color: trace.uiColor,
      content: (
        <Box>
          <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
            {trace.runName}
          </Typography>
          <Typography variant="caption" display="block">
            Date Range: {trace.dateFrom} to {trace.dateTo}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption">
              Points: {trace.tracePoints.length}
            </Typography>
            <Button
              size="small"
              disabled={isLoading}
              text
              onClick={() => {
                const trc = traces[idx]

                if (trc) {
                  const fromDate = dayjs(trc.dateFrom)
                  const toDate = dayjs(trc.dateTo)

                  loadReplayData(trc.runId, trc.runName, trc.uiColor, fromDate, toDate)
                }
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      ),
      data: { index: idx },
    }
    )), [isLoading, traces, loadReplayData])

  // Handle tab change in TabbedWindow
  const handleTabChange = useCallback((newIndex: number) => {
    setActiveTraceIndex(newIndex)
  }, [setActiveTraceIndex])

  // Handle tab close in TabbedWindow
  const handleTabClose = useCallback((index: number) => {
    clearReplayData(index)
  }, [clearReplayData])

  // Handle add tab in TabbedWindow
  const handleAddTab = useCallback(() => {
    handleOpenSettings()
  }, [handleOpenSettings])

  // Handle playback speed adjustment
  const handleDecreaseSpeed = useCallback(() => {
    setPlaybackSpeed(Math.max(0.5, playbackSpeed - 0.5))
  }, [playbackSpeed, setPlaybackSpeed])

  const handleIncreaseSpeed = useCallback(() => {
    setPlaybackSpeed(Math.min(5, playbackSpeed + 0.5))
  }, [playbackSpeed, setPlaybackSpeed])

  // Handle jumping to start/end
  const handleJumpToStart = useCallback(() => {
    jumpToPoint(0)
  }, [jumpToPoint])

  const handleJumpToEnd = useCallback(() => {
    if (activeTraceIndex === null) return

    const activeTrace = traces[activeTraceIndex]

    if (!activeTrace) return
    jumpToPoint(activeTrace.tracePoints.length - 1)
  }, [activeTraceIndex, traces, jumpToPoint])

  // Handle slider change
  const handleSliderChange = useCallback((_: Event, value: number | number[]) => {
    jumpToPoint(value as number)
  }, [jumpToPoint])

  // Get current active trace
  const activeTrace = useMemo(() =>
    activeTraceIndex !== null ? traces[activeTraceIndex] : null
  , [traces, activeTraceIndex])

  // Calculate current point time
  const currentPointTime = useMemo(() => {
    if (!activeTrace || !activeTrace.tracePoints[currentPointIndex]) return ''

    return new Date(activeTrace.tracePoints[currentPointIndex].deviceTime).toLocaleString()
  }, [activeTrace, currentPointIndex])

  // Empty state content for TabbedWindow
  const emptyStateContent = (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>No historical trace data loaded</Typography>
      <Button
        outlined
        onClick={handleOpenSettings}
        startIcon={<Icon icon={faPlus} />}
      >
        Add Historical Trace
      </Button>
    </Box>
  )

  return (
    <Paper
      sx={{
        position: 'absolute',
        bottom: '12px',
        left: '12px',
        zIndex: 1000,
        padding: pxToRem(12),
        width: '350px',
        maxHeight: 'calc(100% - 48px)', // Limit height to 80% of viewport height
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent overflow from the Paper
      }}
    >
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
        flexShrink: 0,
      }}>
        <Typography variant="subtitle1">Historical Replay</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={toggleCollapse} sx={{ ml: 1 }}>
            <Icon icon={isCollapsed ? faUpRightAndDownLeftFromCenter : faDownLeftAndUpRightToCenter} sx={{ fontSize: pxToRem(14) }} />
          </IconButton>
          <IconButton size="small" onClick={handleClosePanel}>
            <Icon icon={faTimes} sx={{ fontSize: pxToRem(16) }} />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs section */}
      <Box sx={{
        mb: 1,
        height: 'auto',
        maxHeight: '130px',
        flexShrink: 0,
      }}>
        <TabbedWindow
          tabs={tabItems}
          activeTabIndex={tabItems.length === 0 ? null : activeTraceIndex}
          onTabChange={handleTabChange}
          onTabClose={handleTabClose}
          onAddTab={handleAddTab}
          showAddButton={true}
          disableTabRipple={true}
          maxLabelWidth={80}
          emptyStateContent={isCollapsed ? null : emptyStateContent}
        />
      </Box>

      {/* Collapsible content */}
      {!isCollapsed && activeTrace && (
        <Box sx={{
          overflow: 'auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Divider sx={{ mb: 2, flexShrink: 0 }} />

          <Box sx={{ mb: 2, flexShrink: 0 }}>
            <Typography variant="caption" display="block">Playback Controls</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <IconButton onClick={handleJumpToStart} disabled={!activeTrace}>
                <Icon icon={faStepBackward} />
              </IconButton>

              <IconButton onClick={toggleReplay} disabled={!activeTrace}>
                <Icon icon={isReplaying ? faPause : faPlay} />
              </IconButton>

              <IconButton onClick={handleJumpToEnd} disabled={!activeTrace}>
                <Icon icon={faStepForward} />
              </IconButton>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <Icon icon={faTachometerAlt} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  {playbackSpeed}x
                </Typography>
              </Box>

              <Box sx={{ ml: 2 }}>
                <IconButton
                  size="small"
                  onClick={handleDecreaseSpeed}
                  disabled={playbackSpeed <= 0.5}
                >
                  -
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleIncreaseSpeed}
                  disabled={playbackSpeed >= 5}
                >
                  +
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Slider
                min={0}
                max={activeTrace.tracePoints.length - 1}
                value={currentPointIndex}
                onChange={handleSliderChange}
                disabled={!activeTrace}
              />

              {currentPointTime && (
                <Typography variant="caption" display="block" align="center">
                  {currentPointTime}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2, flexShrink: 0 }} />

          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="caption" display="block">Layer Controls</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon sx={{ fontSize: pxToRem(16) }} icon={faMapMarker} />
                <Typography variant="body2" sx={{ ml: 1 }}>Processed Points</Typography>
              </Box>
              <Switch
                checked={showProcessedPoints}
                onChange={toggleProcessedPoints}
                size="small"
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon sx={{ fontSize: pxToRem(16) }} icon={faMapMarker} />
                <Typography variant="body2" sx={{ ml: 1 }}>Raw GPS Points</Typography>
              </Box>
              <Switch
                checked={showRawPoints}
                onChange={toggleRawPoints}
                size="small"
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon sx={{ fontSize: pxToRem(16) }} icon={faRoute} />
                <Typography variant="body2" sx={{ ml: 1 }}>Route Path</Typography>
              </Box>
              <Switch
                checked={showRoute}
                onChange={toggleRoute}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      )}

      <ReplaySettingsDialog
        open={isSettingsDialogOpen}
        onClose={handleCloseSettings}
      />
    </Paper>
  )
}